import React, { useRef, useState } from 'react';
import { Upload, FileText, X, Loader, Eye } from 'lucide-react';
import api from '../../services/api';
import FilePreviewModal from './FilePreviewModal';

/**
 * FileUploadField
 *
 * Props:
 *   label                  — field label text
 *   serviceUid             — UID of the service to scope the upload to
 *   value                  — current file uid string, or null
 *   onChange(uid)          — called with new file uid after upload, or null on remove
 *   onUploadingChange(bool)— notifies parent when an upload is in progress
 *   disabled               — locks all interaction
 */
const FileUploadField = ({ label, serviceUid, value, onChange, onUploadingChange, disabled = false }) => {
    const inputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [localFileName, setLocalFileName] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const setUploading = (state) => {
        setIsUploading(state);
        onUploadingChange?.(state);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset input so the same file can be re-selected after removal
        e.target.value = '';

        setUploadError('');
        setUploading(true);

        try {
            const result = await api.uploadFile(serviceUid, file);
            setLocalFileName(file.name);
            onChange(result.uid);
        } catch (err) {
            setUploadError(err.message || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        setUploadError('');
        setLocalFileName(null);
        onChange(null);
    };

    const handleAreaClick = () => {
        if (!disabled && !isUploading && !value) {
            inputRef.current?.click();
        }
    };

    const handleAreaKeyDown = (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled && !isUploading && !value) {
            e.preventDefault();
            inputRef.current?.click();
        }
    };

    return (
        <div className="space-y-1">
            {label && (
                <label className="label-base flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />
                    {label}
                </label>
            )}

            <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf,.jpg,.jpeg,image/jpeg,.png,image/png"
                className="hidden"
                onChange={handleFileChange}
                disabled={disabled || isUploading}
            />

            {value ? (
                /* Uploaded state */
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-surface-border bg-surface-overlay text-sm text-text-primary">
                    <FileText className="w-4 h-4 text-accent flex-shrink-0" />
                    <span className="flex-1 truncate font-mono text-xs text-text-muted" title={localFileName ?? value}>
                        {localFileName ?? 'Attached file'}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                        className="p-0.5 text-text-muted hover:text-accent rounded transition-colors flex-shrink-0"
                        title="Preview file"
                    >
                        <Eye className="w-3.5 h-3.5" />
                    </button>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="p-0.5 text-text-muted hover:text-status-overdue rounded transition-colors flex-shrink-0"
                            title="Remove file"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
            ) : (
                /* Drop / upload area */
                <div
                    onClick={handleAreaClick}
                    onKeyDown={handleAreaKeyDown}
                    role="button"
                    tabIndex={disabled || isUploading ? -1 : 0}
                    className={[
                        'flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-md border border-dashed transition-colors text-center',
                        disabled || isUploading
                            ? 'border-surface-border bg-surface-overlay cursor-not-allowed opacity-60'
                            : 'border-surface-border bg-surface-overlay/40 hover:border-accent/40 hover:bg-surface-overlay cursor-pointer',
                    ].join(' ')}
                >
                    {isUploading ? (
                        <>
                            <Loader className="w-5 h-5 text-accent animate-spin" />
                            <span className="text-xs text-text-muted">Uploading…</span>
                        </>
                    ) : (
                        <>
                            <Upload className="w-5 h-5 text-text-muted" />
                            <span className="text-xs text-text-muted">
                                Click to upload file
                            </span>
                            <span className="text-[10px] text-text-muted/60">PDF, JPEG or PNG — max 10 MB</span>
                        </>
                    )}
                </div>
            )}

            {uploadError && (
                <span className="text-xs text-status-overdue mt-1 block">{uploadError}</span>
            )}

            {previewOpen && (
                <FilePreviewModal
                    fileUid={value}
                    label={label}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </div>
    );
};

export default FileUploadField;
